import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { contractorsAPI } from '@/lib/api-client'
import { formatDocument } from '@/lib/utils'
import { usePermission } from '@/hooks/usePermission'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ContractorFormDialog } from './contractors/ContractorFormDialog'
import {
  Plus,
  Search,
  HardHat,
  Star,
  Users,
  ChevronLeft,
  ChevronRight,
  Loader2,
  UserX,
  LayoutGrid,
  List,
} from 'lucide-react'

function renderStars(rating: number) {
  const stars = []
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <Star
        key={i}
        className={`h-3.5 w-3.5 ${i <= rating ? 'fill-amber-400 text-amber-400' : 'text-neutral-300'}`}
      />
    )
  }
  return stars
}

export function Contractors() {
  const navigate = useNavigate()
  const canCreate = usePermission('contractors:create')

  const [viewMode, setViewMode] = useState<'card' | 'table'>(() =>
    (localStorage.getItem('viewMode:contractors') as 'card' | 'table') ?? 'card'
  )
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [specialtyFilter, setSpecialtyFilter] = useState('')
  const [specialtyInput, setSpecialtyInput] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')

  const limit = viewMode === 'table' ? 15 : 9

  const { data, isLoading } = useQuery({
    queryKey: ['contractors', { page, search, specialty: specialtyFilter, isActive: statusFilter, limit }],
    queryFn: () =>
      contractorsAPI.list({
        page,
        limit,
        search: search || undefined,
        specialty: specialtyFilter || undefined,
        isActive: statusFilter === '' ? undefined : statusFilter === 'true',
      }),
  })

  const contractors = data?.data || []
  const pagination = data?.pagination || { page: 1, totalPages: 1, total: 0 }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
    setSpecialtyFilter(specialtyInput)
    setPage(1)
  }

  const handleViewMode = (v: 'card' | 'table') => {
    localStorage.setItem('viewMode:contractors', v)
    setViewMode(v)
    setPage(1)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Empreiteiros</h1>
          <p className="mt-1 text-neutral-600">Gerencie os empreiteiros cadastrados</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <div className="flex items-center rounded-lg border p-0.5">
            <Button
              variant={viewMode === 'card' ? 'default' : 'ghost'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => handleViewMode('card')}
            >
              <LayoutGrid className="mr-1 h-3.5 w-3.5" />Cards
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              className="h-7 text-xs"
              onClick={() => handleViewMode('table')}
            >
              <List className="mr-1 h-3.5 w-3.5" />Tabela
            </Button>
          </div>
          {canCreate && (
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Novo Empreiteiro
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <form onSubmit={handleSearch} className="flex flex-1 gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Buscar empreiteiros..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-10"
            />
          </div>
          <Input
            placeholder="Filtrar por especialidade..."
            value={specialtyInput}
            onChange={(e) => setSpecialtyInput(e.target.value)}
            className="w-full sm:w-[220px]"
          />
          <Button type="submit" variant="secondary">Buscar</Button>
        </form>

        <Select
          value={statusFilter}
          onValueChange={(value) => { setStatusFilter(value === 'ALL' ? '' : value); setPage(1) }}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Filtrar por status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Todos</SelectItem>
            <SelectItem value="true">Ativo</SelectItem>
            <SelectItem value="false">Inativo</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : contractors.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border bg-neutral-50 p-16">
          <UserX className="h-16 w-16 text-neutral-300" />
          <h3 className="mt-4 text-xl font-medium text-neutral-900">
            {search || specialtyFilter || statusFilter ? 'Nenhum empreiteiro encontrado' : 'Nenhum empreiteiro cadastrado'}
          </h3>
          <p className="mt-2 text-neutral-500">
            {search || specialtyFilter || statusFilter ? 'Tente ajustar os filtros de busca.' : 'Comece cadastrando seu primeiro empreiteiro.'}
          </p>
          {canCreate && !search && !specialtyFilter && !statusFilter && (
            <Button className="mt-6" onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />Cadastrar Primeiro Empreiteiro
            </Button>
          )}
        </div>
      ) : viewMode === 'card' ? (
        <>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {contractors.map((contractor: any) => (
              <Card
                key={contractor.id}
                className="cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => navigate(`/contractors/${contractor.id}`)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <HardHat className="h-5 w-5 text-primary" />
                      <CardTitle className="text-base line-clamp-1">{contractor.name}</CardTitle>
                    </div>
                    <Badge variant={contractor.isActive ? 'default' : 'secondary'}>
                      {contractor.isActive ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-neutral-500">{formatDocument(contractor.document)}</p>
                  {contractor.specialty && contractor.specialty.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {contractor.specialty.slice(0, 3).map((spec: string, idx: number) => (
                        <Badge key={idx} variant="outline" className="text-xs">{spec}</Badge>
                      ))}
                      {contractor.specialty.length > 3 && (
                        <Badge variant="outline" className="text-xs">+{contractor.specialty.length - 3}</Badge>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    {renderStars(contractor.rating || 0)}
                    <span className="ml-1 text-sm text-neutral-500">({contractor.rating || 0}/5)</span>
                  </div>
                  {contractor.teamSize != null && (
                    <div className="flex items-center gap-1 text-sm text-neutral-500">
                      <Users className="h-3.5 w-3.5" />
                      <span>{contractor.teamSize} pessoa(s) na equipe</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-neutral-500">
                Mostrando {contractors.length} de {pagination.total} empreiteiros
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />Anterior
                </Button>
                <span className="text-sm text-neutral-600">{page} / {pagination.totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>
                  Próximo<ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="rounded-lg border bg-white overflow-x-auto">
            <div className="min-w-[600px]">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-50">
                  <TableHead className="font-semibold">Nome</TableHead>
                  <TableHead className="font-semibold">CNPJ</TableHead>
                  <TableHead className="font-semibold">Especialidades</TableHead>
                  <TableHead className="font-semibold text-center">Avaliação</TableHead>
                  <TableHead className="font-semibold text-center">Equipe</TableHead>
                  <TableHead className="font-semibold text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contractors.map((contractor: any) => (
                  <TableRow
                    key={contractor.id}
                    className="cursor-pointer hover:bg-neutral-50"
                    onClick={() => navigate(`/contractors/${contractor.id}`)}
                  >
                    <TableCell className="font-medium">{contractor.name}</TableCell>
                    <TableCell className="text-neutral-500 text-sm">{formatDocument(contractor.document) || '—'}</TableCell>
                    <TableCell>
                      {contractor.specialty && contractor.specialty.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {contractor.specialty.slice(0, 2).map((spec: string, idx: number) => (
                            <Badge key={idx} variant="outline" className="text-xs">{spec}</Badge>
                          ))}
                          {contractor.specialty.length > 2 && (
                            <Badge variant="outline" className="text-xs">+{contractor.specialty.length - 2}</Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-neutral-400 text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                        <span className="text-sm">{contractor.rating || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center text-sm text-neutral-600">
                      {contractor.teamSize ?? '—'}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={contractor.isActive ? 'default' : 'secondary'}>
                        {contractor.isActive ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </div>

          {pagination.totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-neutral-500">
                Mostrando {contractors.length} de {pagination.total} empreiteiros
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />Anterior
                </Button>
                <span className="text-sm text-neutral-600">{page} / {pagination.totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>
                  Próximo<ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <ContractorFormDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} />
    </div>
  )
}
